import type { FeatureSegment, FeatureStrand } from "@db/content";
import type { GenbankLocation } from "./parse";

/** A GenBank location resolved into a strand and flattened segments. */
export type ResolvedLocation = {
  strand: FeatureStrand;
  segments: FeatureSegment[];
};

/**
 * Resolve a GenBank location into flattened segments and strand.
 * Handles complement(), join(), ranges, and single positions.
 */
export function resolveLocation(location: GenbankLocation): ResolvedLocation {
  return resolveLocationRecursive(location, false);
}

function resolveLocationRecursive(
  location: GenbankLocation,
  isComplement: boolean,
): ResolvedLocation {
  switch (location.type) {
    case "single":
      return {
        strand: isComplement ? "reverse" : "forward",
        segments: [{ start: location.position, end: location.position }],
      };

    case "range":
      return {
        strand: isComplement ? "reverse" : "forward",
        segments: [{ start: location.start, end: location.end }],
      };

    case "complement":
      return resolveLocationRecursive(location.location, true);

    case "join": {
      const resolved = location.locations.map((loc) =>
        resolveLocationRecursive(loc, isComplement),
      );
      const segments = resolved.flatMap((r) => r.segments);
      return {
        strand: isComplement ? "reverse" : "forward",
        segments,
      };
    }
  }
}
